import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('subject', formData.subject);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d67i10vmvg9ih2c79600', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <section id="contact" className="relative bg-gradient-to-br from-[#1A0B2E] to-[#2D1554] py-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-4 gap-16 mb-32">
          {/* Column 1 */}
          <div>
            <h2 className="text-2xl text-white font-light mb-4">JANI d.o.o.</h2>
            <p className="text-sm text-white/60 leading-relaxed">
              Expert team developing modern iOS applications focused on security and performance enhancement for mobile devices.
            </p>
          </div>

          {/* Column 2 - Location */}
          <div>
            <h3 className="text-xs uppercase tracking-widest text-white/60 mb-6">LOCATION</h3>
            <div className="space-y-3 text-white">
              <p className="flex items-start gap-2">
                <i className="ri-map-pin-line text-lg text-purple-400 flex-shrink-0 mt-0.5"></i>
                <span>Kralja Kresimira 36</span>
              </p>
              <p className="flex items-start gap-2">
                <i className="ri-building-line text-lg text-purple-400 flex-shrink-0 mt-0.5"></i>
                <span>City Pozega</span>
              </p>
              <p className="flex items-start gap-2">
                <i className="ri-mail-line text-lg text-purple-400 flex-shrink-0 mt-0.5"></i>
                <span>Zip 34000</span>
              </p>
              <p className="flex items-start gap-2">
                <i className="ri-global-line text-lg text-purple-400 flex-shrink-0 mt-0.5"></i>
                <span>Country Croatia</span>
              </p>
            </div>
          </div>

          {/* Column 3 - Contact */}
          <div>
            <h3 className="text-xs uppercase tracking-widest text-white/60 mb-6">CONTACT</h3>
            <div className="space-y-3 text-white">
              <p className="flex items-start gap-2">
                <i className="ri-mail-line text-lg text-purple-400 flex-shrink-0 mt-0.5"></i>
                <a href="mailto:contact@janidoo.com" className="hover:text-purple-300 transition-colors">
                  Email: contact@janidoo.com
                </a>
              </p>
              <p className="flex items-start gap-2">
                <i className="ri-phone-line text-lg text-purple-400 flex-shrink-0 mt-0.5"></i>
                <a href="tel:+15074167855" className="hover:text-purple-300 transition-colors">
                  Number: +1 5074167855
                </a>
              </p>
            </div>
          </div>

          {/* Column 4 - Contact Form */}
          <div>
            <h3 className="text-xs uppercase tracking-widest text-white/60 mb-6">GET IN TOUCH</h3>
            <form id="contact-form" onSubmit={handleSubmit} data-readdy-form className="space-y-4">
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-purple-400 transition-colors text-sm"
              />
              <input
                type="email"
                name="email"
                placeholder="Your Email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-purple-400 transition-colors text-sm"
              />
              <input
                type="text"
                name="subject"
                placeholder="Subject"
                value={formData.subject}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-purple-400 transition-colors text-sm"
              />
              <textarea
                name="message"
                placeholder="Your Message"
                value={formData.message}
                onChange={handleChange}
                required
                maxLength={500}
                rows={3}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-purple-400 transition-colors resize-none text-sm"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-6 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium whitespace-nowrap"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
              {submitStatus === 'success' && (
                <p className="text-green-400 text-sm">Message sent successfully!</p>
              )}
              {submitStatus === 'error' && (
                <p className="text-red-400 text-sm">Failed to send message. Please try again.</p>
              )}
            </form>
          </div>
        </div>

        {/* Large Brand Typography */}
        <div className="text-center">
          <div className="text-[140px] font-bold text-[#F5F1E8] leading-none tracking-tight" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
            JANI
          </div>
        </div>
      </div>

      {/* Footer Bottom */}
      <div className="max-w-7xl mx-auto px-6 mt-12 pt-8 border-t border-white/10">
        <div className="flex justify-between items-center text-sm text-white/60">
          <p>&copy; 2025 JANI d.o.o. All rights reserved.</p>
          <a 
            href="https://readdy.ai/?ref=logo" 
            target="_blank" 
            rel="nofollow noopener noreferrer"
            className="hover:text-white/80 transition-colors"
          >
            Powered by Readdy
          </a>
        </div>
      </div>
    </section>
  );
}
